import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Calendar, Lock, Unlock, Edit, Trash2, Eye, Search, 
  Users, Calculator, Printer, CheckCircle, RefreshCw, ChevronRight,
  DollarSign, Hash, Save, Receipt, FileText, Download, FileSpreadsheet
} from 'lucide-react';

// Fixed Muasaat rate
const MUASAAT_RATE = 215;

// Helper function for Indian number format (₹00,00,000)
const formatIndian = (num) => {
  if (num === null || num === undefined) return '0';
  return Number(num).toLocaleString('en-IN');
};

export default function WajebaatPage() {
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({ name: '', sila_fitra_amount: '' });

  const fetchYears = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/wajebaat/years');
      setYears(response.data.years || []);
    } catch (error) {
      toast.error('Failed to fetch years');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchYears();
  }, [fetchYears]);

  const handleCreateYear = async () => {
    if (!formData.name || !formData.sila_fitra_amount) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      await api.post('/wajebaat/years', {
        name: formData.name,
        sila_fitra_amount: parseFloat(formData.sila_fitra_amount)
      });
      toast.success('Year created successfully');
      setShowCreateDialog(false);
      setFormData({ name: '', sila_fitra_amount: '' });
      fetchYears();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create year');
    }
  };

  const handleEditYear = async () => {
    if (!formData.name || !formData.sila_fitra_amount) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      await api.put(`/wajebaat/years/${editingYear.id}`, {
        name: formData.name,
        sila_fitra_amount: parseFloat(formData.sila_fitra_amount)
      });
      toast.success('Year updated successfully');
      setShowEditDialog(false);
      setEditingYear(null);
      setFormData({ name: '', sila_fitra_amount: '' });
      fetchYears();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update year');
    }
  };

  const handleActivate = async (year) => {
    try {
      await api.post(`/wajebaat/years/${year.id}/activate`);
      toast.success(`${year.name} activated`);
      fetchYears();
    } catch (error) {
      toast.error('Failed to activate year');
    }
  };

  const handleDeactivate = async (year) => {
    try {
      await api.post(`/wajebaat/years/${year.id}/deactivate`);
      toast.success(`${year.name} deactivated`);
      fetchYears();
    } catch (error) {
      toast.error('Failed to deactivate year');
    }
  };

  const handleLock = async (year) => {
    try {
      await api.post(`/wajebaat/years/${year.id}/lock`);
      toast.success(`${year.name} locked`);
      fetchYears();
    } catch (error) {
      toast.error('Failed to lock year');
    }
  };

  const handleUnlock = async (year) => {
    try {
      await api.post(`/wajebaat/years/${year.id}/unlock`);
      toast.success(`${year.name} unlocked`);
      fetchYears();
    } catch (error) {
      toast.error('Failed to unlock year');
    }
  };

  const handleDelete = async (year) => {
    if (!window.confirm(`Delete ${year.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/wajebaat/years/${year.id}`);
      toast.success('Year deleted');
      fetchYears();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete year');
    }
  };

  const openEditDialog = (year) => {
    setEditingYear(year);
    setFormData({ name: year.name, sila_fitra_amount: year.sila_fitra_amount.toString() });
    setShowEditDialog(true);
  };

  if (selectedYear) {
    return (
      <SilaFitraEntryPage 
        year={selectedYear} 
        api={api} 
        user={user}
        isAdmin={isAdmin}
        onBack={() => {
          setSelectedYear(null);
          fetchYears();
        }} 
      />
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Wajebaat</h1>
          <p className="text-slate-500 mt-1">Manage Wajebaat Bethak Years & Sila/Fitra</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreateDialog(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Create Year
          </Button>
        )}
      </div>

      {/* Years Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : years.length === 0 ? (
        <Card className="p-12 text-center">
          <Calendar className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-600">No Wajebaat Years</h3>
          <p className="text-slate-400 mt-2">Create your first Wajebaat year to get started</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {years.map((year) => (
            <Card 
              key={year.id} 
              className={`relative transition-all hover:shadow-lg ${
                year.is_active ? 'border-2 border-green-500 bg-green-50/30' : ''
              } ${year.is_locked ? 'opacity-75' : ''}`}
            >
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{year.name}</CardTitle>
                  </div>
                  <div className="flex gap-1">
                    {year.is_active && (
                      <Badge className="bg-green-500">Active</Badge>
                    )}
                    {year.is_locked && (
                      <Badge variant="secondary"><Lock className="w-3 h-3" /></Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-slate-50 p-2 rounded">
                    <span className="text-slate-500">Entries</span>
                    <p className="font-semibold">{year.entries_count || 0}</p>
                  </div>
                  <div className="bg-slate-50 p-2 rounded">
                    <span className="text-slate-500">Total</span>
                    <p className="font-semibold">Rs. {(year.total_amount || 0).toLocaleString('en-IN')}</p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => setSelectedYear(year)}
                    className="flex-1"
                    disabled={year.is_locked && !isAdmin}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Open
                  </Button>
                  
                  {isAdmin && (
                    <>
                      {!year.is_locked && (
                        <Button size="sm" variant="outline" onClick={() => openEditDialog(year)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {year.is_active ? (
                        <Button size="sm" variant="outline" onClick={() => handleDeactivate(year)}>
                          Deactivate
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleActivate(year)}>
                          Activate
                        </Button>
                      )}
                      
                      {year.is_locked ? (
                        <Button size="sm" variant="outline" onClick={() => handleUnlock(year)}>
                          <Unlock className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => handleLock(year)}>
                          <Lock className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {!year.is_locked && (year.entries_count || 0) === 0 && (
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(year)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Wajebaat Year</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Year Name</Label>
              <Input
                placeholder="e.g., Wajebaat 1447H"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Sila/Fitra Amount (per unit)</Label>
              <Input
                type="number"
                placeholder="e.g., 500"
                value={formData.sila_fitra_amount}
                onChange={(e) => setFormData({ ...formData, sila_fitra_amount: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateYear}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Wajebaat Year</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Year Name</Label>
              <Input
                placeholder="e.g., Wajebaat 1447H"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Sila/Fitra Amount (per unit)</Label>
              <Input
                type="number"
                placeholder="e.g., 500"
                value={formData.sila_fitra_amount}
                onChange={(e) => setFormData({ ...formData, sila_fitra_amount: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditYear}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== SILA/FITRA ENTRY PAGE ====================

function SilaFitraEntryPage({ year, api, user, isAdmin, onBack }) {
  const [activeTab, setActiveTab] = useState('entry');
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedHof, setSelectedHof] = useState(null);
  const [familyData, setFamilyData] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [counts, setCounts] = useState({ mardo: 0, bairo: 0, gair_balig: 0 });
  const [additionalFields, setAdditionalFields] = useState({ hamal: 0, amwat: 0 });
  const [saving, setSaving] = useState(false);
  const [existingEntry, setExistingEntry] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  
  // Bluetooth print state
  const [bluetoothDevice, setBluetoothDevice] = useState(null);
  const [printerCharacteristic, setPrinterCharacteristic] = useState(null);
  const [printing, setPrinting] = useState(false);
  
  // Wajebaat Amounts Popup State (NEW)
  const [showAmountsPopup, setShowAmountsPopup] = useState(false);
  const [savedEntryForPopup, setSavedEntryForPopup] = useState(null);
  const [wajebaatAmounts, setWajebaatAmounts] = useState({
    wajebaat_amount: '',
    takarubaat: '',
    muasaat_units: ''
  });
  const [savingAmounts, setSavingAmounts] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState(''); // 'pdf' or 'excel'
  const [exportFilter, setExportFilter] = useState('all'); // 'all', 'sila_only', 'wajebaat_only'
  const [selectedColumns, setSelectedColumns] = useState({
    date: true,
    hof_name: true,
    its: true,
    mohalla: true,
    mardo: true,
    bairo: true,
    gair_balig: true,
    units: true,
    sila_fitra: true,
    wajebaat: true,
    takarubaat: true,
    muasaat: true,
    grand_total: true
  });
  
  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Calculate totals
  const totalUnits = (counts.mardo * 2) + (counts.bairo * 2) + counts.gair_balig + additionalFields.hamal + additionalFields.amwat;
  const totalAmount = totalUnits * year.sila_fitra_amount;
  
  // Calculate popup totals (real-time)
  const popupMuasaatTotal = useMemo(() => {
    const units = parseInt(wajebaatAmounts.muasaat_units) || 0;
    return units * MUASAAT_RATE;
  }, [wajebaatAmounts.muasaat_units]);
  
  const popupGrandTotal = useMemo(() => {
    const silaFitra = savedEntryForPopup?.total_amount || 0;
    const wajebaat = parseFloat(wajebaatAmounts.wajebaat_amount) || 0;
    const takarubaat = parseFloat(wajebaatAmounts.takarubaat) || 0;
    return silaFitra + wajebaat + takarubaat + popupMuasaatTotal;
  }, [savedEntryForPopup, wajebaatAmounts, popupMuasaatTotal]);

  // Fetch entries
  const fetchEntries = useCallback(async () => {
    try {
      setEntriesLoading(true);
      const response = await api.get(`/wajebaat/years/${year.id}/entries`);
      setEntries(response.data.entries || []);
    } catch (error) {
      toast.error('Failed to fetch entries');
    } finally {
      setEntriesLoading(false);
    }
  }, [api, year.id]);

  // Open export dialog
  const openExportDialog = (format) => {
    setExportFormat(format);
    setExportFilter('all');
    // Reset columns to all selected
    setSelectedColumns({
      date: true,
      hof_name: true,
      its: true,
      mohalla: true,
      mardo: true,
      bairo: true,
      gair_balig: true,
      units: true,
      sila_fitra: true,
      wajebaat: true,
      takarubaat: true,
      muasaat: true,
      grand_total: true
    });
    setShowExportDialog(true);
  };

  // Toggle column selection
  const toggleColumn = (col) => {
    setSelectedColumns(prev => ({...prev, [col]: !prev[col]}));
  };

  // Select/Deselect all columns
  const selectAllColumns = (select) => {
    const newCols = {};
    Object.keys(selectedColumns).forEach(k => newCols[k] = select);
    setSelectedColumns(newCols);
  };

  // Export with filter and column selection
  const handleExport = async () => {
    if (entries.length === 0) {
      toast.error('No entries to export');
      return;
    }
    
    setExporting(true);
    setShowExportDialog(false);
    
    try {
      // Build columns parameter
      const columnsParam = Object.entries(selectedColumns)
        .filter(([_, v]) => v)
        .map(([k, _]) => k)
        .join(',');
      
      const endpoint = exportFormat === 'excel' 
        ? `/wajebaat/years/${year.id}/entries/export/excel`
        : `/wajebaat/years/${year.id}/entries/export/pdf`;
      
      const response = await api.get(`${endpoint}?filter=${exportFilter}&columns=${columnsParam}`, {
        responseType: 'blob'
      });
      
      const filterLabel = exportFilter === 'sila_only' ? 'SilaFitra' : 
                          exportFilter === 'wajebaat_only' ? 'Wajebaat' : 'All';
      const ext = exportFormat === 'excel' ? 'xlsx' : 'pdf';
      const filename = `${year.name}_${filterLabel}_Entries.${ext}`;
      
      const url = window.URL.createObjectURL(new Blob([response.data], { 
        type: exportFormat === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${exportFormat.toUpperCase()} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${exportFormat.toUpperCase()}`);
    } finally {
      setExporting(false);
    }
  };

  // Export to Excel (legacy - now uses dialog)
  const handleExportExcel = async () => {
    openExportDialog('excel');
  };

  // Export to PDF (legacy - now uses dialog)
  const handleExportPDF = async () => {
    openExportDialog('pdf');
  };

  // Delete Wajebaat entry
  const handleDeleteEntry = async () => {
    if (!entryToDelete) return;
    
    try {
      setDeleting(true);
      await api.delete(`/wajebaat/years/${year.id}/entries/${entryToDelete.id}`);
      toast.success('Entry deleted successfully');
      setShowDeleteConfirm(false);
      setEntryToDelete(null);
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'entries') {
      fetchEntries();
    }
  }, [activeTab, fetchEntries]);

  // Search HOF
  useEffect(() => {
    const searchHof = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }
      
      try {
        setSearching(true);
        const response = await api.get(`/wajebaat/years/${year.id}/search-hof?query=${encodeURIComponent(searchQuery)}`);
        setSearchResults(response.data.results || []);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setSearching(false);
      }
    };

    const debounce = setTimeout(searchHof, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, api, year.id]);

  // Select HOF and fetch family
  const handleSelectHof = async (hof) => {
    setSelectedHof(hof);
    setSearchQuery('');
    setSearchResults([]);
    
    try {
      const response = await api.get(`/wajebaat/years/${year.id}/family/${hof.its_id}`);
      setFamilyData(response.data);
      
      if (response.data.existing_entry) {
        setExistingEntry(response.data.existing_entry);
        // Pre-fill from existing entry
        setSelectedMembers(response.data.existing_entry.selected_member_its || []);
        setCounts({
          mardo: response.data.existing_entry.mardo,
          bairo: response.data.existing_entry.bairo,
          gair_balig: response.data.existing_entry.gair_balig
        });
        setAdditionalFields({
          hamal: response.data.existing_entry.hamal || 0,
          amwat: response.data.existing_entry.amwat || 0
        });
      } else {
        // Select all members by default
        const allIts = response.data.members.map(m => m.its_id);
        setSelectedMembers(allIts);
        calculateCounts(allIts, response.data.members);
        setExistingEntry(null);
      }
    } catch (error) {
      toast.error('Failed to fetch family data');
    }
  };

  // Calculate counts based on selected members
  const calculateCounts = (selectedIts, members) => {
    const selected = members.filter(m => selectedIts.includes(m.its_id));
    
    let mardo = 0, bairo = 0, gair_balig = 0;
    
    selected.forEach(member => {
      const isMisaqDone = member.misaq_status?.toLowerCase() === 'done';
      const isMale = member.gender?.toLowerCase() === 'male';
      
      if (isMisaqDone) {
        if (isMale) mardo++;
        else bairo++;
      } else {
        gair_balig++;
      }
    });
    
    setCounts({ mardo, bairo, gair_balig });
  };

  // Handle member selection
  const handleMemberToggle = (its_id) => {
    const newSelected = selectedMembers.includes(its_id)
      ? selectedMembers.filter(id => id !== its_id)
      : [...selectedMembers, its_id];
    
    setSelectedMembers(newSelected);
    if (familyData?.members) {
      calculateCounts(newSelected, familyData.members);
    }
  };

  // Select all members
  const handleSelectAll = () => {
    if (familyData?.members) {
      const allIts = familyData.members.map(m => m.its_id);
      setSelectedMembers(allIts);
      calculateCounts(allIts, familyData.members);
    }
  };

  // Deselect all
  const handleDeselectAll = () => {
    setSelectedMembers([]);
    setCounts({ mardo: 0, bairo: 0, gair_balig: 0 });
  };

  // Save entry
  const handleSave = async () => {
    if (!selectedHof || !familyData) {
      toast.error('Please select a HOF first');
      return;
    }
    
    if (year.is_locked) {
      toast.error('Year is locked. Cannot save entries.');
      return;
    }

    try {
      setSaving(true);
      
      const entryData = {
        hof_its: selectedHof.its_id,
        hof_name: selectedHof.full_name,
        mohalla: familyData.family.mohalla,
        sector: familyData.family.sector,
        selected_member_its: selectedMembers,
        mardo: counts.mardo,
        bairo: counts.bairo,
        gair_balig: counts.gair_balig,
        hamal: additionalFields.hamal,
        amwat: additionalFields.amwat,
        total_units: totalUnits,
        total_amount: totalAmount
      };

      let savedEntry;
      if (existingEntry) {
        // Update existing
        const response = await api.put(`/wajebaat/years/${year.id}/entries/${existingEntry.id}`, entryData);
        savedEntry = response.data;
        toast.success('Entry updated successfully');
      } else {
        // Create new
        const response = await api.post(`/wajebaat/years/${year.id}/entries`, entryData);
        savedEntry = response.data;
        
        // Show appropriate message based on token sync result
        const syncResult = savedEntry.token_sync;
        if (syncResult?.action === 'token_auto_generated_completed') {
          toast.success(`Entry saved! Token #${syncResult.token_number} auto-generated & marked complete.`);
        } else if (syncResult?.action === 'token_auto_completed') {
          toast.success('Entry saved! Existing token marked as Takhmeen complete.');
        } else if (syncResult?.action === 'already_completed') {
          toast.success('Entry saved! Token was already completed.');
        } else if (syncResult?.action === 'no_active_event') {
          toast.success('Entry saved! (No active event to sync token)');
        } else {
          toast.success('Entry saved successfully.');
        }
      }
      
      setExistingEntry(savedEntry);
      
      // Open the Wajebaat Amounts popup automatically
      setSavedEntryForPopup(savedEntry);
      // Pre-fill if existing amounts
      setWajebaatAmounts({
        wajebaat_amount: savedEntry.wajebaat_amount?.toString() || '',
        takarubaat: savedEntry.takarubaat?.toString() || '',
        muasaat_units: savedEntry.muasaat_units?.toString() || ''
      });
      setShowAmountsPopup(true);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setSelectedHof(null);
    setFamilyData(null);
    setSelectedMembers([]);
    setCounts({ mardo: 0, bairo: 0, gair_balig: 0 });
    setAdditionalFields({ hamal: 0, amwat: 0 });
    setExistingEntry(null);
    setSearchQuery('');
  };

  // Handle saving Wajebaat amounts from popup
  const handleSaveAmounts = async () => {
    if (!savedEntryForPopup) return;
    
    // Validate
    const wajebaat = parseFloat(wajebaatAmounts.wajebaat_amount) || 0;
    const takarubaat = parseFloat(wajebaatAmounts.takarubaat) || 0;
    const muasaat = parseInt(wajebaatAmounts.muasaat_units) || 0;
    
    if (wajebaat < 0 || takarubaat < 0 || muasaat < 0) {
      toast.error('Amounts cannot be negative');
      return;
    }
    
    if (wajebaatAmounts.muasaat_units && !Number.isInteger(parseFloat(wajebaatAmounts.muasaat_units))) {
      toast.error('Muasaat units must be a whole number');
      return;
    }
    
    try {
      setSavingAmounts(true);
      
      const response = await api.put(
        `/wajebaat/years/${year.id}/entries/${savedEntryForPopup.id}/amounts`,
        {
          wajebaat_amount: wajebaat,
          takarubaat: takarubaat,
          muasaat_units: muasaat
        }
      );
      
      const updatedEntry = response.data;
      setSavedEntryForPopup(updatedEntry);
      setExistingEntry(updatedEntry);
      
      // Show token sync result
      const syncResult = updatedEntry.token_sync;
      if (syncResult?.action === 'token_auto_generated_completed') {
        toast.success(`Amounts saved! Token #${syncResult.token_number} auto-generated & Takhmeen marked done.`);
      } else if (syncResult?.action === 'token_auto_completed') {
        toast.success('Amounts saved! HOF Takhmeen marked as done in Token Events.');
      } else if (syncResult?.action === 'already_completed') {
        toast.success('Amounts saved! Token was already marked as Takhmeen done.');
      } else if (syncResult?.action === 'no_active_event') {
        toast.success('Amounts saved! (No active token event to sync)');
      } else {
        toast.success('Amounts saved successfully!');
      }
      
      // Now print both slips automatically
      await handleDualSlipPrint(updatedEntry);
      
      // Close popup and reset
      setShowAmountsPopup(false);
      setWajebaatAmounts({ wajebaat_amount: '', takarubaat: '', muasaat_units: '' });
      resetForm();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save amounts');
    } finally {
      setSavingAmounts(false);
    }
  };

  // Skip amounts and just print basic slip via Bluetooth
  const handleSkipAmounts = async () => {
    if (savedEntryForPopup) {
      // Print basic slip only via Bluetooth
      await handlePrint(savedEntryForPopup);
    }
    setShowAmountsPopup(false);
    setWajebaatAmounts({ wajebaat_amount: '', takarubaat: '', muasaat_units: '' });
    resetForm();
  };

  // Dual slip printing - Print both detailed and summary slips via Bluetooth
  const handleDualSlipPrint = async (entry) => {
    setPrinting(true);
    try {
      // Connect to Bluetooth printer first
      let char = printerCharacteristic;
      if (!char || !bluetoothDevice?.gatt?.connected) {
        char = await connectBluetoothPrinter();
      }
      
      if (!char) {
        toast.error('Please connect Bluetooth printer first');
        setPrinting(false);
        return;
      }
      
      // Print Slip 1 (Detailed) via Bluetooth
      toast.info('Printing Slip 1 (Detailed)...');
      const detailedData = generateDetailedSlipData(entry);
      await sendToPrinter(char, detailedData);
      
      // Wait 3 seconds between slips for paper to come out
      toast.info('Waiting for Slip 1... Printing Slip 2 in 3 seconds');
      await new Promise(r => setTimeout(r, 3000));
      
      // Print Slip 2 (Summary) via Bluetooth
      toast.info('Printing Slip 2 (Summary)...');
      const summaryData = generateSummarySlipData(entry);
      await sendToPrinter(char, summaryData);
      
      toast.success('Both slips printed successfully!');
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to print slips. Please check printer connection.');
    } finally {
      setPrinting(false);
    }
  };

  // Send data to Bluetooth printer in chunks
  const sendToPrinter = async (char, printData) => {
    const chunkSize = 100;
    for (let i = 0; i < printData.length; i += chunkSize) {
      const chunk = printData.slice(i, i + chunkSize);
      if (char.properties.writeWithoutResponse) {
        await char.writeValueWithoutResponse(chunk);
      } else {
        await char.writeValue(chunk);
      }
      await new Promise(r => setTimeout(r, 50));
    }
  };

  // Generate ESC/POS data for Detailed Slip (Slip 1)
  const generateDetailedSlipData = (entry) => {
    const encoder = new TextEncoder();
    const commands = [];
    const ESC = 0x1B, GS = 0x1D, LF = 0x0A;
    
    const silaFitra = entry.total_amount || 0;
    const wajebaat = entry.wajebaat_amount || 0;
    const takarubaat = entry.takarubaat || 0;
    const muasaatUnits = entry.muasaat_units || 0;
    const muasaatTotal = entry.muasaat_total || (muasaatUnits * MUASAAT_RATE);
    const grandTotal = entry.grand_total || (silaFitra + wajebaat + takarubaat + muasaatTotal);
    const dateStr = entry.entry_date || new Date().toLocaleDateString('en-IN');
    const timeStr = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    const yearNum = year.name?.replace('Wajebaat ', '') || '';
    
    // Helper to pad strings for alignment
    const padRight = (str, len) => String(str).padEnd(len);
    
    // Initialize printer
    commands.push(ESC, 0x40);
    
    // Line separator
    const line = '----------------------------------------';
    
    // Header
    commands.push(ESC, 0x61, 0x01); // Center
    commands.push(...encoder.encode(line)); commands.push(LF);
    commands.push(ESC, 0x45, 0x01); // Bold
    commands.push(...encoder.encode(`WAJEBAAT BETHAK ${yearNum}`)); commands.push(LF);
    commands.push(ESC, 0x45, 0x00);
    commands.push(...encoder.encode('DETAILED COPY')); commands.push(LF);
    commands.push(...encoder.encode(line)); commands.push(LF);
    
    // HOF Info
    commands.push(ESC, 0x61, 0x00); // Left
    commands.push(...encoder.encode(`Date: ${dateStr}`)); commands.push(LF);
    commands.push(ESC, 0x45, 0x01);
    commands.push(...encoder.encode((entry.hof_name || '').substring(0, 32))); commands.push(LF);
    commands.push(ESC, 0x45, 0x00);
    commands.push(...encoder.encode(`ITS: ${entry.hof_its}`)); commands.push(LF);
    commands.push(...encoder.encode((entry.mohalla || '').toUpperCase())); commands.push(LF);
    commands.push(ESC, 0x61, 0x01);
    commands.push(...encoder.encode(line)); commands.push(LF);
    
    // SILA / FITRA Section
    commands.push(...encoder.encode('SILA / FITRA')); commands.push(LF);
    commands.push(...encoder.encode(line)); commands.push(LF);
    
    // Column format: Item (20) + Count (5) + Total (7)
    commands.push(ESC, 0x61, 0x00); // Left
    commands.push(...encoder.encode(`${padRight('Mardo', 18)}${padRight(entry.mardo, 6)}${entry.mardo} x 2 = ${entry.mardo * 2}`)); commands.push(LF);
    commands.push(...encoder.encode(`${padRight('Bairo', 18)}${padRight(entry.bairo, 6)}${entry.bairo} x 2 = ${entry.bairo * 2}`)); commands.push(LF);
    commands.push(...encoder.encode(`${padRight('Gair Balig', 18)}${padRight(entry.gair_balig, 6)}${entry.gair_balig} x 1 = ${entry.gair_balig}`)); commands.push(LF);
    commands.push(...encoder.encode(`${padRight('Hamal', 18)}${padRight(entry.hamal || 0, 6)}${entry.hamal || 0} x 1 = ${entry.hamal || 0}`)); commands.push(LF);
    commands.push(...encoder.encode(`${padRight('Amwat', 18)}${padRight(entry.amwat || 0, 6)}${entry.amwat || 0} x 1 = ${entry.amwat || 0}`)); commands.push(LF);
    commands.push(ESC, 0x61, 0x01);
    commands.push(...encoder.encode(line)); commands.push(LF);
    
    // Totals
    commands.push(ESC, 0x61, 0x00);
    commands.push(...encoder.encode(`${padRight('TOTAL UNITS', 24)}${entry.total_units}`)); commands.push(LF);
    commands.push(...encoder.encode(`${padRight('PER UNIT', 20)}Rs. ${formatIndian(year.sila_fitra_amount)}`)); commands.push(LF);
    commands.push(ESC, 0x45, 0x01);
    commands.push(...encoder.encode(`${padRight('Sila/Fitra Total', 16)}Rs. ${formatIndian(silaFitra)}`)); commands.push(LF);
    commands.push(ESC, 0x45, 0x00);
    commands.push(ESC, 0x61, 0x01);
    commands.push(...encoder.encode(line)); commands.push(LF);
    
    // ADDITIONAL AMOUNTS Section
    commands.push(...encoder.encode('ADDITIONAL AMOUNTS')); commands.push(LF);
    commands.push(...encoder.encode(line)); commands.push(LF);
    
    commands.push(ESC, 0x61, 0x00);
    commands.push(...encoder.encode(`${padRight('Wajebaat Amt', 20)}Rs. ${formatIndian(wajebaat)}`)); commands.push(LF);
    commands.push(...encoder.encode(`${padRight('Takarubaat', 20)}Rs. ${formatIndian(takarubaat)}`)); commands.push(LF);
    commands.push(...encoder.encode(`${padRight(`Muasaat (${muasaatUnits} x ${MUASAAT_RATE})`, 20)}Rs. ${formatIndian(muasaatTotal)}`)); commands.push(LF);
    commands.push(ESC, 0x61, 0x01);
    commands.push(...encoder.encode(line)); commands.push(LF);
    
    // GRAND TOTAL
    commands.push(ESC, 0x45, 0x01);
    commands.push(GS, 0x21, 0x01); // Double height
    commands.push(...encoder.encode(`GRAND TOTAL     Rs. ${formatIndian(grandTotal)}`)); commands.push(LF);
    commands.push(GS, 0x21, 0x00);
    commands.push(ESC, 0x45, 0x00);
    commands.push(...encoder.encode(line)); commands.push(LF);
    
    // Footer
    commands.push(...encoder.encode(`${dateStr}  ${timeStr}`)); commands.push(LF);
    commands.push(...encoder.encode(line)); commands.push(LF);
    commands.push(LF, LF, LF); // Feed paper
    
    return new Uint8Array(commands);
  };

  // Generate ESC/POS data for Summary Slip (Slip 2)
  const generateSummarySlipData = (entry) => {
    const encoder = new TextEncoder();
    const commands = [];
    const ESC = 0x1B, GS = 0x1D, LF = 0x0A;
    
    const silaFitra = entry.total_amount || 0;
    const wajebaat = entry.wajebaat_amount || 0;
    const takarubaat = entry.takarubaat || 0;
    const muasaatUnits = entry.muasaat_units || 0;
    const muasaatTotal = entry.muasaat_total || (muasaatUnits * MUASAAT_RATE);
    const grandTotal = entry.grand_total || (silaFitra + wajebaat + takarubaat + muasaatTotal);
    const dateStr = entry.entry_date || new Date().toLocaleDateString('en-IN');
    const timeStr = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    const yearNum = year.name?.replace('Wajebaat ', '') || '';
    
    // Line separator
    const line = '----------------------------------------';
    
    // Initialize printer
    commands.push(ESC, 0x40);
    
    // Header
    commands.push(ESC, 0x61, 0x01); // Center
    commands.push(...encoder.encode(line)); commands.push(LF);
    commands.push(ESC, 0x45, 0x01);
    commands.push(...encoder.encode(`WAJEBAAT BETHAK ${yearNum}`)); commands.push(LF);
    commands.push(ESC, 0x45, 0x00);
    commands.push(...encoder.encode(line)); commands.push(LF);
    
    // HOF Details
    commands.push(ESC, 0x61, 0x00); // Left
    commands.push(...encoder.encode('Name:')); commands.push(LF);
    commands.push(ESC, 0x45, 0x01);
    commands.push(...encoder.encode((entry.hof_name || '').substring(0, 32))); commands.push(LF);
    commands.push(ESC, 0x45, 0x00);
    commands.push(LF);
    commands.push(...encoder.encode('ITS:')); commands.push(LF);
    commands.push(ESC, 0x45, 0x01);
    commands.push(...encoder.encode(entry.hof_its)); commands.push(LF);
    commands.push(ESC, 0x45, 0x00);
    commands.push(LF);
    commands.push(...encoder.encode('Mohalla:')); commands.push(LF);
    commands.push(ESC, 0x45, 0x01);
    commands.push(...encoder.encode(entry.mohalla || '-')); commands.push(LF);
    commands.push(ESC, 0x45, 0x00);
    commands.push(LF);
    commands.push(...encoder.encode('Sector No:')); commands.push(LF);
    commands.push(ESC, 0x45, 0x01);
    commands.push(...encoder.encode(entry.sector || '-')); commands.push(LF);
    commands.push(ESC, 0x45, 0x00);
    
    // Separator and Total
    commands.push(ESC, 0x61, 0x01); // Center
    commands.push(...encoder.encode(line)); commands.push(LF);
    commands.push(LF);
    commands.push(ESC, 0x45, 0x01);
    commands.push(...encoder.encode('TOTAL PAYABLE AMOUNT')); commands.push(LF);
    commands.push(LF);
    commands.push(GS, 0x21, 0x11); // Double width + height
    commands.push(...encoder.encode(`Rs. ${formatIndian(grandTotal)}`)); commands.push(LF);
    commands.push(GS, 0x21, 0x00);
    commands.push(ESC, 0x45, 0x00);
    commands.push(LF);
    commands.push(...encoder.encode(line)); commands.push(LF);
    commands.push(...encoder.encode(`${dateStr}   ${timeStr}`)); commands.push(LF);
    commands.push(...encoder.encode(line)); commands.push(LF);
    commands.push(LF, LF, LF); // Feed paper
    
    return new Uint8Array(commands);
  };

  // Slip 1: Detailed slip - Browser fallback (kept for reference)
  const printDetailedSlip = (entry) => {
    const silaFitra = entry.total_amount || 0;
    const wajebaat = entry.wajebaat_amount || 0;
    const takarubaat = entry.takarubaat || 0;
    const muasaatUnits = entry.muasaat_units || 0;
    const muasaatTotal = entry.muasaat_total || (muasaatUnits * MUASAAT_RATE);
    const grandTotal = entry.grand_total || (silaFitra + wajebaat + takarubaat + muasaatTotal);
    const dateStr = entry.entry_date || new Date().toLocaleDateString('en-IN');
    const timeStr = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          @page { size: 58mm auto; margin: 2mm; }
          body { 
            font-family: 'Courier New', monospace; 
            font-size: 10px; 
            width: 54mm; 
            margin: 0; 
            padding: 2mm;
            line-height: 1.3;
          }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .divider { text-align: center; margin: 2px 0; }
          .row { display: flex; justify-content: space-between; margin: 1px 0; }
          .row-3col { display: flex; justify-content: space-between; margin: 1px 0; }
          .row-3col span:first-child { flex: 2; text-align: left; }
          .row-3col span:nth-child(2) { flex: 1; text-align: center; }
          .row-3col span:last-child { flex: 1; text-align: right; }
          .big { font-size: 12px; }
          .huge { font-size: 14px; }
          .section-title { text-align: center; font-weight: bold; margin: 4px 0 2px 0; }
          .grand-total-box { 
            border: 1px solid #000; 
            padding: 4px; 
            margin: 4px 0;
            display: flex;
            justify-content: space-between;
            font-weight: bold;
            font-size: 12px;
          }
        </style>
      </head>
      <body>
        <div class="center bold big">WAJEBAAT SLIP</div>
        <div class="center">${year.name}</div>
        <div class="center" style="font-size:9px;">DETAILED COPY</div>
        <div class="divider">----------------------------------------</div>
        
        <div>Date: ${dateStr}</div>
        <div class="bold">${entry.hof_name}</div>
        <div>ITS: ${entry.hof_its}</div>
        <div>${entry.mohalla || ''}</div>
        
        <div class="divider">----------------------------------------</div>
        <div class="section-title">SILA / FITRA</div>
        <div class="divider">----------------------------------------</div>
        
        <div class="row-3col">
          <span>Mardo (${entry.mardo}×2)</span>
          <span></span>
          <span>${entry.mardo * 2}</span>
        </div>
        <div class="row-3col">
          <span>Bairo (${entry.bairo}×2)</span>
          <span></span>
          <span>${entry.bairo * 2}</span>
        </div>
        <div class="row-3col">
          <span>Gair Balig</span>
          <span></span>
          <span>${entry.gair_balig}</span>
        </div>
        <div class="row-3col">
          <span>Hamal</span>
          <span></span>
          <span>${entry.hamal || 0}</span>
        </div>
        <div class="row-3col">
          <span>Amwat</span>
          <span></span>
          <span>${entry.amwat || 0}</span>
        </div>
        <div class="divider">----------------------------------------</div>
        <div class="row"><span>Total Units</span><span>${entry.total_units}</span></div>
        <div class="row"><span>Rate</span><span>Rs.${formatIndian(year.sila_fitra_amount)}</span></div>
        <div class="row bold"><span>Sila/Fitra</span><span>Rs.${formatIndian(silaFitra)}</span></div>
        
        <div class="divider">----------------------------------------</div>
        <div class="section-title">ADDITIONAL AMOUNTS</div>
        <div class="divider">----------------------------------------</div>
        
        <div class="row"><span>Wajebaat Amt</span><span>Rs.${formatIndian(wajebaat)}</span></div>
        <div class="row"><span>Takarubaat</span><span>Rs.${formatIndian(takarubaat)}</span></div>
        <div class="row"><span>Muasaat (${muasaatUnits}×${MUASAAT_RATE})</span><span>Rs.${formatIndian(muasaatTotal)}</span></div>
        
        <div class="divider">----------------------------------------</div>
        
        <div class="grand-total-box">
          <span>GRAND TOTAL</span>
          <span>Rs.${formatIndian(grandTotal)}</span>
        </div>
        
        <div class="divider">----------------------------------------</div>
        <div class="center" style="font-size:9px;">
          ${dateStr}, ${timeStr}
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=300,height=600');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => printWindow.print();
    }
  };

  // Slip 2: Summary slip with only Grand Total
  const printSummarySlip = (entry) => {
    const silaFitra = entry.total_amount || 0;
    const wajebaat = entry.wajebaat_amount || 0;
    const takarubaat = entry.takarubaat || 0;
    const muasaatUnits = entry.muasaat_units || 0;
    const muasaatTotal = entry.muasaat_total || (muasaatUnits * MUASAAT_RATE);
    const grandTotal = entry.grand_total || (silaFitra + wajebaat + takarubaat + muasaatTotal);
    const dateStr = entry.entry_date || new Date().toLocaleDateString('en-IN');
    const timeStr = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          @page { size: 58mm auto; margin: 2mm; }
          body { 
            font-family: 'Courier New', monospace; 
            font-size: 10px; 
            width: 54mm; 
            margin: 0; 
            padding: 2mm;
            line-height: 1.4;
          }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .divider { text-align: center; margin: 3px 0; }
          .big { font-size: 12px; }
          .huge { font-size: 20px; }
          .label { font-size: 9px; color: #333; }
          .value { font-weight: bold; }
          .field { margin: 4px 0; }
        </style>
      </head>
      <body>
        <div class="divider">----------------------------------------</div>
        <div class="center bold big">WAJEBAAT BETHAK ${year.name?.replace('Wajebaat ', '') || ''}</div>
        <div class="divider">----------------------------------------</div>
        
        <div class="field">
          <div class="label">Name:</div>
          <div class="value">${entry.hof_name}</div>
        </div>
        
        <div class="field">
          <div class="label">ITS:</div>
          <div class="value">${entry.hof_its}</div>
        </div>
        
        <div class="field">
          <div class="label">Mohalla:</div>
          <div class="value">${entry.mohalla || '-'}</div>
        </div>
        
        <div class="field">
          <div class="label">Sector No:</div>
          <div class="value">${entry.sector || '-'}</div>
        </div>
        
        <div class="divider">----------------------------------------</div>
        
        <div class="center bold" style="margin: 6px 0;">TOTAL PAYABLE AMOUNT</div>
        
        <div class="center bold huge" style="padding: 8px 0;">
          Rs. ${formatIndian(grandTotal)}
        </div>
        
        <div class="divider">----------------------------------------</div>
        <div class="center" style="font-size:9px;">
          ${dateStr}   ${timeStr}
        </div>
        <div class="divider">----------------------------------------</div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=300,height=500');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => printWindow.print();
    }
  };

  // Bluetooth print
  const connectBluetoothPrinter = async () => {
    try {
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          '000018f0-0000-1000-8000-00805f9b34fb',
          '49535343-fe7d-4ae5-8fa9-9fafd205e455',
          'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
          '0000ff00-0000-1000-8000-00805f9b34fb',
          '0000ffe0-0000-1000-8000-00805f9b34fb',
          '0000fff0-0000-1000-8000-00805f9b34fb',
        ]
      });
      
      setBluetoothDevice(device);
      const server = await device.gatt.connect();
      
      // Find writable characteristic
      const serviceUUIDs = [
        '000018f0-0000-1000-8000-00805f9b34fb',
        '49535343-fe7d-4ae5-8fa9-9fafd205e455',
        '0000ff00-0000-1000-8000-00805f9b34fb',
        '0000ffe0-0000-1000-8000-00805f9b34fb',
        '0000fff0-0000-1000-8000-00805f9b34fb',
      ];
      
      for (const uuid of serviceUUIDs) {
        try {
          const service = await server.getPrimaryService(uuid);
          const chars = await service.getCharacteristics();
          for (const char of chars) {
            if (char.properties.write || char.properties.writeWithoutResponse) {
              setPrinterCharacteristic(char);
              toast.success('Printer connected!');
              return char;
            }
          }
        } catch (e) { continue; }
      }
      
      toast.error('Printer connected but not writable. Try browser print.');
      return null;
    } catch (error) {
      if (error.name !== 'NotFoundError') {
        toast.error('Failed to connect printer');
      }
      return null;
    }
  };

  const generatePrintData = (entry) => {
    const encoder = new TextEncoder();
    const commands = [];
    const ESC = 0x1B, GS = 0x1D, LF = 0x0A;
    
    // Initialize
    commands.push(ESC, 0x40);
    
    // Header
    commands.push(ESC, 0x61, 0x01); // Center
    commands.push(ESC, 0x45, 0x01); // Bold
    commands.push(GS, 0x21, 0x01);
    commands.push(...encoder.encode('SILA / FITRA'));
    commands.push(LF);
    commands.push(GS, 0x21, 0x00);
    commands.push(...encoder.encode(year.name));
    commands.push(LF);
    commands.push(ESC, 0x45, 0x00);
    commands.push(...encoder.encode('================================'));
    commands.push(LF);
    
    // HOF Info
    commands.push(ESC, 0x61, 0x00); // Left
    commands.push(...encoder.encode(`Date: ${entry.entry_date || new Date().toLocaleDateString('en-IN')}`));
    commands.push(LF);
    commands.push(ESC, 0x45, 0x01);
    commands.push(...encoder.encode((entry.hof_name || '').substring(0, 28)));
    commands.push(LF);
    commands.push(ESC, 0x45, 0x00);
    commands.push(...encoder.encode(`ITS: ${entry.hof_its}`));
    commands.push(LF);
    commands.push(...encoder.encode('--------------------------------'));
    commands.push(LF);
    
    // Counts - Show count = result (without multiplier text)
    commands.push(...encoder.encode(`Mardo         : ${entry.mardo} = ${entry.mardo * 2}`));
    commands.push(LF);
    commands.push(...encoder.encode(`Bairo         : ${entry.bairo} = ${entry.bairo * 2}`));
    commands.push(LF);
    commands.push(...encoder.encode(`Gair Balig    : ${entry.gair_balig} = ${entry.gair_balig}`));
    commands.push(LF);
    commands.push(...encoder.encode(`Hamal         : ${entry.hamal} = ${entry.hamal}`));
    commands.push(LF);
    commands.push(...encoder.encode(`Amwat         : ${entry.amwat} = ${entry.amwat}`));
    commands.push(LF);
    commands.push(...encoder.encode('--------------------------------'));
    commands.push(LF);
    
    // Totals
    commands.push(ESC, 0x45, 0x01);
    commands.push(...encoder.encode(`Total Units   : ${entry.total_units}`));
    commands.push(LF);
    commands.push(...encoder.encode(`Per Unit      : Rs.${entry.per_unit_amount || year.sila_fitra_amount}`));
    commands.push(LF);
    commands.push(GS, 0x21, 0x01);
    commands.push(...encoder.encode(`TOTAL: Rs.${entry.total_amount?.toLocaleString('en-IN')}`));
    commands.push(LF);
    commands.push(GS, 0x21, 0x00);
    commands.push(ESC, 0x45, 0x00);
    commands.push(...encoder.encode('================================'));
    commands.push(LF);
    
    // Footer
    commands.push(ESC, 0x61, 0x01);
    commands.push(...encoder.encode(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })));
    commands.push(LF, LF, LF, LF);
    
    return new Uint8Array(commands);
  };

  const handlePrint = async (entry) => {
    setPrinting(true);
    try {
      let char = printerCharacteristic;
      if (!char || !bluetoothDevice?.gatt?.connected) {
        char = await connectBluetoothPrinter();
      }
      
      if (!char) {
        toast.error('Please connect Bluetooth printer first');
        setPrinting(false);
        return;
      }
      
      const printData = generatePrintData(entry);
      await sendToPrinter(char, printData);
      
      toast.success('Printed successfully!');
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to print. Please check printer connection.');
    } finally {
      setPrinting(false);
    }
  };

  const handleBrowserPrint = (entry) => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          @page { size: 58mm auto; margin: 2mm; }
          body { font-family: 'Courier New', monospace; font-size: 10px; width: 54mm; }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .divider { border-top: 1px dashed #000; margin: 4px 0; }
          .row { display: flex; justify-content: space-between; }
          .big { font-size: 14px; }
        </style>
      </head>
      <body>
        <div class="center bold big">SILA / FITRA</div>
        <div class="center">${year.name}</div>
        <div class="divider"></div>
        <div>Date: ${entry.entry_date || new Date().toLocaleDateString('en-IN')}</div>
        <div class="bold">${entry.hof_name}</div>
        <div>ITS: ${entry.hof_its}</div>
        <div class="divider"></div>
        <div class="row"><span>Mardo</span><span>${entry.mardo} = ${entry.mardo * 2}</span></div>
        <div class="row"><span>Bairo</span><span>${entry.bairo} = ${entry.bairo * 2}</span></div>
        <div class="row"><span>Gair Balig</span><span>${entry.gair_balig} = ${entry.gair_balig}</span></div>
        <div class="row"><span>Hamal</span><span>${entry.hamal} = ${entry.hamal}</span></div>
        <div class="row"><span>Amwat</span><span>${entry.amwat} = ${entry.amwat}</span></div>
        <div class="divider"></div>
        <div class="row bold"><span>Total Units</span><span>${entry.total_units}</span></div>
        <div class="row"><span>Per Unit</span><span>Rs.${entry.per_unit_amount || year.sila_fitra_amount}</span></div>
        <div class="row bold big"><span>TOTAL</span><span>Rs.${entry.total_amount?.toLocaleString('en-IN')}</span></div>
        <div class="divider"></div>
        <div class="center">${new Date().toLocaleString('en-IN')}</div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=300,height=500');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.onload = () => printWindow.print();
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={onBack}>
            <ChevronRight className="w-4 h-4 rotate-180" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">{year.name}</h1>
          </div>
        </div>
        <div className="flex gap-2">
          {year.is_active && <Badge className="bg-green-500">Active</Badge>}
          {year.is_locked && <Badge variant="secondary"><Lock className="w-3 h-3 mr-1" /> Locked</Badge>}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="entry">
            <Plus className="w-4 h-4 mr-1" /> New Entry
          </TabsTrigger>
          <TabsTrigger value="entries">
            <Users className="w-4 h-4 mr-1" /> All Entries ({entries.length})
          </TabsTrigger>
        </TabsList>

        {/* New Entry Tab */}
        <TabsContent value="entry" className="space-y-6">
          {year.is_locked ? (
            <Card className="p-8 text-center">
              <Lock className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium">Year is Locked</h3>
              <p className="text-slate-500">No new entries or edits allowed</p>
            </Card>
          ) : (
            <>
              {/* Search HOF */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Search className="w-5 h-5" /> Search HOF
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <Input
                      placeholder="Search by ITS Number or Name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pr-10"
                    />
                    {searching && (
                      <RefreshCw className="w-4 h-4 absolute right-3 top-3 animate-spin text-slate-400" />
                    )}
                  </div>
                  
                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="mt-2 border rounded-lg divide-y max-h-60 overflow-auto">
                      {searchResults.map((hof) => (
                        <div
                          key={hof.its_id}
                          className="p-3 hover:bg-slate-50 cursor-pointer flex justify-between items-center"
                          onClick={() => handleSelectHof(hof)}
                        >
                          <div>
                            <p className="font-medium">{hof.full_name}</p>
                            <p className="text-sm text-slate-500">ITS: {hof.its_id} | {hof.mohalla} | {hof.sector}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Family Details */}
              {selectedHof && familyData && (
                <>
                  <Card>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">{selectedHof.full_name}</CardTitle>
                          <CardDescription>
                            ITS: {selectedHof.its_id} | {familyData.family.mohalla} | {familyData.family.sector}
                          </CardDescription>
                        </div>
                        <div className="flex gap-2">
                          {existingEntry && (
                            <Badge variant="secondary">Existing Entry</Badge>
                          )}
                          <Button size="sm" variant="outline" onClick={resetForm}>
                            Clear
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Member Selection */}
                      <div className="flex justify-between items-center mb-3">
                        <Label className="font-medium">Select Members ({selectedMembers.length}/{familyData.members.length})</Label>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={handleSelectAll}>Select All</Button>
                          <Button size="sm" variant="outline" onClick={handleDeselectAll}>Clear</Button>
                        </div>
                      </div>
                      
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10"></TableHead>
                              <TableHead>ITS</TableHead>
                              <TableHead>Name</TableHead>
                              <TableHead>Gender</TableHead>
                              <TableHead>Misaq</TableHead>
                              <TableHead>Age</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {familyData.members.map((member) => (
                              <TableRow 
                                key={member.its_id}
                                className={selectedMembers.includes(member.its_id) ? 'bg-blue-50' : ''}
                              >
                                <TableCell>
                                  <Checkbox
                                    checked={selectedMembers.includes(member.its_id)}
                                    onCheckedChange={() => handleMemberToggle(member.its_id)}
                                  />
                                </TableCell>
                                <TableCell className="font-mono text-sm">{member.its_id}</TableCell>
                                <TableCell>{member.full_name}</TableCell>
                                <TableCell>{member.gender}</TableCell>
                                <TableCell>
                                  <Badge variant={member.misaq_status?.toLowerCase() === 'done' ? 'default' : 'secondary'}>
                                    {member.misaq_status || 'N/A'}
                                  </Badge>
                                </TableCell>
                                <TableCell>{member.age || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Counts & Calculation */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Counts */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Calculator className="w-5 h-5" /> Member Counts
                        </CardTitle>
                        <CardDescription>Auto-calculated based on selection. Edit if needed.</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label>Mardo (Male Misaq Done)</Label>
                            <Input
                              type="number"
                              min="0"
                              value={counts.mardo}
                              onChange={(e) => setCounts({ ...counts, mardo: parseInt(e.target.value) || 0 })}
                              className="mt-1"
                            />
                            <p className="text-xs text-slate-500 mt-1">x2 units</p>
                          </div>
                          <div>
                            <Label>Bairo (Female Misaq Done)</Label>
                            <Input
                              type="number"
                              min="0"
                              value={counts.bairo}
                              onChange={(e) => setCounts({ ...counts, bairo: parseInt(e.target.value) || 0 })}
                              className="mt-1"
                            />
                            <p className="text-xs text-slate-500 mt-1">x2 units</p>
                          </div>
                          <div>
                            <Label>Gair Balig (Non Misaq)</Label>
                            <Input
                              type="number"
                              min="0"
                              value={counts.gair_balig}
                              onChange={(e) => setCounts({ ...counts, gair_balig: parseInt(e.target.value) || 0 })}
                              className="mt-1"
                            />
                            <p className="text-xs text-slate-500 mt-1">x1 unit</p>
                          </div>
                        </div>
                        
                        <div className="border-t pt-4">
                          <Label className="font-medium">Additional Fields</Label>
                          <div className="grid grid-cols-2 gap-4 mt-2">
                            <div>
                              <Label className="text-sm">Hamal</Label>
                              <Input
                                type="number"
                                min="0"
                                value={additionalFields.hamal}
                                onChange={(e) => setAdditionalFields({ ...additionalFields, hamal: parseInt(e.target.value) || 0 })}
                                className="mt-1"
                              />
                              <p className="text-xs text-slate-500 mt-1">x1 unit</p>
                            </div>
                            <div>
                              <Label className="text-sm">Amwat</Label>
                              <Input
                                type="number"
                                min="0"
                                value={additionalFields.amwat}
                                onChange={(e) => setAdditionalFields({ ...additionalFields, amwat: parseInt(e.target.value) || 0 })}
                                className="mt-1"
                              />
                              <p className="text-xs text-slate-500 mt-1">x1 unit</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Calculation Summary */}
                    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <DollarSign className="w-5 h-5" /> Calculation Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="bg-white/60 rounded-lg p-3 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Mardo ({counts.mardo} x 2)</span>
                            <span>{counts.mardo * 2}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Bairo ({counts.bairo} x 2)</span>
                            <span>{counts.bairo * 2}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Gair Balig ({counts.gair_balig} x 1)</span>
                            <span>{counts.gair_balig}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Hamal ({additionalFields.hamal} x 1)</span>
                            <span>{additionalFields.hamal}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Amwat ({additionalFields.amwat} x 1)</span>
                            <span>{additionalFields.amwat}</span>
                          </div>
                        </div>
                        
                        <div className="border-t border-slate-200 pt-3 space-y-2">
                          <div className="flex justify-between font-medium">
                            <span>Total Units</span>
                            <span className="text-lg">{totalUnits}</span>
                          </div>
                          <div className="flex justify-between text-sm text-slate-600">
                            <span>Per Unit Amount</span>
                            <span>Rs. {year.sila_fitra_amount?.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="flex justify-between font-bold text-xl text-blue-700 pt-2 border-t">
                            <span>Total Amount</span>
                            <span>Rs. {totalAmount.toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                        
                        <Button 
                          className="w-full mt-4" 
                          size="lg"
                          onClick={handleSave}
                          disabled={saving || totalUnits === 0}
                        >
                          {saving ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          {existingEntry ? 'Update Entry' : 'Save Entry'}
                        </Button>
                        
                        {existingEntry && (
                          <Button 
                            variant="outline" 
                            className="w-full"
                            onClick={() => handlePrint(existingEntry)}
                            disabled={printing}
                          >
                            {printing ? (
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Printer className="w-4 h-4 mr-2" />
                            )}
                            Print Slip
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
            </>
          )}
        </TabsContent>

        {/* All Entries Tab */}
        <TabsContent value="entries">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <CardTitle>All Entries ({entries.length})</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleExportExcel}
                    disabled={exporting || entries.length === 0}
                  >
                    {exporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}
                    Excel
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleExportPDF}
                    disabled={exporting || entries.length === 0}
                  >
                    {exporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                    PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={fetchEntries}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {entriesLoading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin" />
                </div>
              ) : entries.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No entries yet
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>HOF</TableHead>
                        <TableHead>ITS</TableHead>
                        <TableHead>Mohalla</TableHead>
                        <TableHead>M</TableHead>
                        <TableHead>B</TableHead>
                        <TableHead>GB</TableHead>
                        <TableHead>Units</TableHead>
                        <TableHead>Sila/Fitra</TableHead>
                        <TableHead>Grand Total</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="text-sm">{entry.entry_date}</TableCell>
                          <TableCell className="font-medium max-w-[150px] truncate">{entry.hof_name}</TableCell>
                          <TableCell className="font-mono text-sm">{entry.hof_its}</TableCell>
                          <TableCell className="text-sm">{entry.mohalla}</TableCell>
                          <TableCell>{entry.mardo}</TableCell>
                          <TableCell>{entry.bairo}</TableCell>
                          <TableCell>{entry.gair_balig}</TableCell>
                          <TableCell className="font-medium">{entry.total_units}</TableCell>
                          <TableCell className="font-medium">Rs. {entry.total_amount?.toLocaleString('en-IN')}</TableCell>
                          <TableCell className="font-bold text-green-600">
                            {entry.grand_total ? `Rs. ${entry.grand_total.toLocaleString('en-IN')}` : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handlePrint(entry)}
                                title="Print basic slip"
                              >
                                <Printer className="w-4 h-4" />
                              </Button>
                              {entry.grand_total && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleDualSlipPrint(entry)}
                                  title="Print detailed + summary slips"
                                  className="text-green-600"
                                >
                                  <Receipt className="w-4 h-4" />
                                </Button>
                              )}
                              {!year.is_locked && (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => {
                                      handleSelectHof({ its_id: entry.hof_its, full_name: entry.hof_name });
                                      setActiveTab('entry');
                                    }}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => { setEntryToDelete(entry); setShowDeleteConfirm(true); }}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title="Delete entry"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Wajebaat Amounts Popup */}
      <Dialog open={showAmountsPopup} onOpenChange={setShowAmountsPopup}>
        <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              Additional Wajebaat Amounts
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Enter optional additional amounts. Leave blank if not applicable.
            </DialogDescription>
          </DialogHeader>
          
          {savedEntryForPopup && (
            <div className="space-y-3 py-1">
              {/* HOF Info Summary */}
              <div className="bg-slate-50 p-2 sm:p-3 rounded-lg">
                <p className="font-medium text-slate-800 text-sm sm:text-base">{savedEntryForPopup.hof_name}</p>
                <p className="text-xs sm:text-sm text-slate-500">ITS: {savedEntryForPopup.hof_its}</p>
                <p className="text-xs sm:text-sm font-medium text-blue-600 mt-1">
                  Sila/Fitra: Rs. {savedEntryForPopup.total_amount?.toLocaleString('en-IN')}
                </p>
              </div>
              
              {/* Amount Inputs */}
              <div className="space-y-2 sm:space-y-3">
                <div>
                  <Label htmlFor="wajebaat_amount" className="text-xs sm:text-sm">Wajebaat Amount (Rs.)</Label>
                  <Input
                    id="wajebaat_amount"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0"
                    value={wajebaatAmounts.wajebaat_amount}
                    onChange={(e) => setWajebaatAmounts(prev => ({ ...prev, wajebaat_amount: e.target.value }))}
                    className="mt-1 h-9 sm:h-10"
                    data-testid="wajebaat-amount-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="takarubaat" className="text-xs sm:text-sm">Takarubaat (Rs.)</Label>
                  <Input
                    id="takarubaat"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0"
                    value={wajebaatAmounts.takarubaat}
                    onChange={(e) => setWajebaatAmounts(prev => ({ ...prev, takarubaat: e.target.value }))}
                    className="mt-1 h-9 sm:h-10"
                    data-testid="takarubaat-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="muasaat_units" className="text-xs sm:text-sm">Muasaat Units</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      id="muasaat_units"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="0"
                      value={wajebaatAmounts.muasaat_units}
                      onChange={(e) => setWajebaatAmounts(prev => ({ ...prev, muasaat_units: e.target.value }))}
                      className="flex-1 h-9 sm:h-10"
                      data-testid="muasaat-units-input"
                    />
                    <span className="text-xs sm:text-sm text-slate-500 whitespace-nowrap">× {MUASAAT_RATE} = Rs. {popupMuasaatTotal.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>
              
              {/* Real-time Calculation Summary */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 sm:p-4 rounded-lg border border-green-200">
                <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
                  <div className="flex justify-between">
                    <span>Sila/Fitra</span>
                    <span>Rs. {(savedEntryForPopup.total_amount || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Wajebaat Amount</span>
                    <span>Rs. {(parseFloat(wajebaatAmounts.wajebaat_amount) || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Takarubaat</span>
                    <span>Rs. {(parseFloat(wajebaatAmounts.takarubaat) || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Muasaat ({parseInt(wajebaatAmounts.muasaat_units) || 0} × {MUASAAT_RATE})</span>
                    <span>Rs. {popupMuasaatTotal.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="border-t border-green-300 pt-2 mt-2">
                    <div className="flex justify-between font-bold text-base sm:text-lg text-green-700">
                      <span>Grand Total</span>
                      <span data-testid="grand-total">Rs. {popupGrandTotal.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex-col gap-2 pt-2 sm:pt-4">
            <Button
              onClick={handleSaveAmounts}
              disabled={savingAmounts}
              className="w-full bg-green-600 hover:bg-green-700 h-10 sm:h-11 text-sm sm:text-base"
              data-testid="save-and-print-btn"
            >
              {savingAmounts ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Printer className="w-4 h-4 mr-2" />
              )}
              Save & Print Both Slips
            </Button>
            <Button
              variant="outline"
              onClick={handleSkipAmounts}
              disabled={savingAmounts}
              className="w-full h-9 sm:h-10 text-xs sm:text-sm"
            >
              <FileText className="w-4 h-4 mr-2" />
              Skip & Print Basic
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Options Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {exportFormat === 'pdf' ? <Download className="w-5 h-5 text-red-600" /> : <FileSpreadsheet className="w-5 h-5 text-emerald-600" />}
              Export {exportFormat === 'pdf' ? 'PDF' : 'Excel'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Filter Options */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Filter Entries</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-2 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                  <input 
                    type="radio" 
                    name="exportFilter" 
                    value="all"
                    checked={exportFilter === 'all'}
                    onChange={(e) => setExportFilter(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div>
                    <p className="font-medium text-sm">All Entries</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-2 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                  <input 
                    type="radio" 
                    name="exportFilter" 
                    value="sila_only"
                    checked={exportFilter === 'sila_only'}
                    onChange={(e) => setExportFilter(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div>
                    <p className="font-medium text-sm">Sila/Fitra Only (no Wajebaat amounts)</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-2 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                  <input 
                    type="radio" 
                    name="exportFilter" 
                    value="wajebaat_only"
                    checked={exportFilter === 'wajebaat_only'}
                    onChange={(e) => setExportFilter(e.target.value)}
                    className="w-4 h-4 text-blue-600"
                  />
                  <div>
                    <p className="font-medium text-sm">Wajebaat Only (entries with amounts)</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Column Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Select Columns to Export</Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => selectAllColumns(true)}>All</Button>
                  <Button variant="outline" size="sm" onClick={() => selectAllColumns(false)}>None</Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg max-h-60 overflow-y-auto">
                {[
                  { key: 'date', label: 'Date' },
                  { key: 'hof_name', label: 'HOF Name' },
                  { key: 'its', label: 'ITS' },
                  { key: 'mohalla', label: 'Mohalla' },
                  { key: 'mardo', label: 'Mardo (M)' },
                  { key: 'bairo', label: 'Bairo (B)' },
                  { key: 'gair_balig', label: 'Gair Balig (GB)' },
                  { key: 'units', label: 'Units' },
                  { key: 'sila_fitra', label: 'Sila/Fitra' },
                  { key: 'wajebaat', label: 'Wajebaat' },
                  { key: 'takarubaat', label: 'Takarubaat' },
                  { key: 'muasaat', label: 'Muasaat' },
                  { key: 'grand_total', label: 'Grand Total' }
                ].map(col => (
                  <label key={col.key} className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded">
                    <input 
                      type="checkbox"
                      checked={selectedColumns[col.key]}
                      onChange={() => toggleColumn(col.key)}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className="text-sm">{col.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExport}
              disabled={exporting || !Object.values(selectedColumns).some(v => v)}
              className={exportFormat === 'pdf' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
            >
              {exporting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : 
               exportFormat === 'pdf' ? <Download className="w-4 h-4 mr-2" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              Export {exportFormat === 'pdf' ? 'PDF' : 'Excel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Wajebaat Entry
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the entry for <strong>{entryToDelete?.hof_name}</strong> (ITS: {entryToDelete?.hof_its})?
              <br /><br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteEntry} 
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
